// src/components/flight-data/PrintButton.js
import React from 'react';
import { Button } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { formatConnectionTime } from '../../utils/connectionUtils';

const PrintButton = ({ tdrData, brockData, params, inboundETA }) => {
  const handlePrint = () => {
    // Open a new window for printing
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert("Please allow popups for this website to use the print feature.");
      return;
    }

    // Helper functions
    // Escape paste-derived/user-typed values before interpolating into the
    // print HTML (written via document.write, so React's escaping doesn't apply)
    const esc = (value) => {
      if (value === null || value === undefined) return '';
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    };

    const displayFlightNumber = (flightNum) => {
      if (!flightNum) return '';
      return flightNum.replace(/^WN\s*/, '');
    };

    const normalizeFlightNumber = (flightNum) => {
      if (!flightNum) return '';
      const match = flightNum.match(/WN\s*(\d+)/i);
      return match ? parseInt(match[1], 10).toString() : flightNum;
    };

    const isOvernightFlight = (flight) => {
      if (flight.isOvernight === true) return true;
      if (flight.status && flight.status.includes("Overnight")) return true;
      return Object.values(flight).some(
        (value) =>
          typeof value === 'string' &&
          (value === "Overnight" || value === "Overnight!" || value.endsWith(" Overnight!"))
      );
    };

    const calculateConnectionTime = (outboundETD, isOvernight = false) => {
      if (!outboundETD || !inboundETA) return null;
      const parseTime = (timeStr) => {
        if (!timeStr) return null;
        const cleanTime = timeStr.replace(/[LZ]/gi, '');
        const [hours, minutes] = cleanTime.split(':').map(Number);
        const total = hours * 60 + minutes;
        // Cancelled flights carry text like "Flt. Cancelled..." in the ETD
        // field — NaN slips past the == null guard and prints as "NaNm"
        return Number.isNaN(total) ? null : total;
      };

      const etaMinutes = parseTime(inboundETA);
      const etdMinutes = parseTime(outboundETD);
      if (etaMinutes == null || etdMinutes == null) return null;

      if (isOvernight) {
        return etdMinutes + 1440 - etaMinutes;
      }

      let diffMinutes = etdMinutes - etaMinutes;
      if (diffMinutes < 0 && diffMinutes > -720) {
        return diffMinutes;
      } else if (diffMinutes < 0) {
        diffMinutes += 24 * 60;
      }
      return diffMinutes;
    };

    const getConnectionCategory = (connectionMinutes) => {
      if (connectionMinutes === null) return 'normal';
      const coldThresholdMinutes = params.coldThresholdHours * 60;

      if (connectionMinutes < 0) {
        return params.isPriorityFlight ? 'priority' : 'hot';
      }
      if (params.isPriorityFlight && connectionMinutes <= 60) {
        return 'priority';
      }
      if (!params.isPriorityFlight && connectionMinutes <= params.hotThreshold) {
        return 'hot';
      }
      if (connectionMinutes >= coldThresholdMinutes) {
        return 'cold';
      }
      return 'normal';
    };

    // UPDATED: Use the same multi-destination logic as CombinedTab
    const mergeAndProcessData = () => {
      const tdrOutbound = tdrData.outbound || [];
      const brockBins = brockData.bins || {};
      const flightMap = new Map();

      // 1) Insert TDR flights
      tdrOutbound.forEach(flight => {
        const normalizedFlightNumber = normalizeFlightNumber(flight.flightNumber);
        const isOvernight = isOvernightFlight(flight);
        const connectionTime = calculateConnectionTime(flight.etd, isOvernight);
        const cleanDestination = flight.destination
          ? flight.destination.replace(/HOT!\s*/g, '').trim()
          : '';

        if (flightMap.has(normalizedFlightNumber)) {
          // Already have a flight with same flightNumber => add more destinations
          const existingFlight = flightMap.get(normalizedFlightNumber);
          if (!existingFlight.destinations) {
            existingFlight.destinations = [{
              city: existingFlight.destination,
              bags: existingFlight.tdrBags,
            }];
            existingFlight.isMultiDestination = true;
          }
          existingFlight.destinations.push({
            city: cleanDestination,
            bags: flight.bags || (flight.destinations?.[0]?.bags || 0),
          });
          existingFlight.tdrBags = existingFlight.destinations.reduce((sum, d) => sum + d.bags, 0);
          existingFlight.totalDestinations = existingFlight.destinations.length;
        } else {
          // New flight
          const destinations = flight.destinations
            ? flight.destinations.map(dest => ({
                city: dest.city ? dest.city.replace(/HOT!\s*/g, '').trim() : '',
                bags: dest.bags,
              }))
            : [{
                city: cleanDestination,
                bags: flight.bags,
              }];

          const isMultiDestination = flight.destinations && flight.destinations.length > 1;
          flightMap.set(normalizedFlightNumber, {
            originalFlightNumber: flight.flightNumber,
            flightNumber: normalizedFlightNumber,
            registration: flight.registration,
            gate: flight.gate,
            etd: flight.etd,
            status: flight.status,
            destination: cleanDestination,
            destinations: isMultiDestination ? destinations : undefined,
            tdrBags: flight.bags,
            brockBags: 0,
            binDistribution: {},
            discrepancy: false,
            isOvernight,
            isMultiDestination,
            totalDestinations: isMultiDestination ? destinations.length : 1,
            connectionTime,
          });
        }
      });

      // 2) Insert Brock flights with proportional redistribution
      Object.entries(brockBins).forEach(([binName, binData]) => {
        (binData.flights || []).forEach(flight => {
          const normalizedFlightNumber = normalizeFlightNumber(flight.flightNumber);
          const cleanDestination = flight.dest ? flight.dest.replace(/HOT!\s*/g, '').trim() : '';
          const isOvernight = isOvernightFlight(flight);
          
          if (flightMap.has(normalizedFlightNumber)) {
            const existingFlight = flightMap.get(normalizedFlightNumber);

            // If multi-destination, match up the correct city
            if (existingFlight.destinations) {
              const destMatch = existingFlight.destinations.find(d => {
                const tdrDest = d.city ? d.city.replace(/HOT!\s*/g, '').trim() : '';
                return tdrDest === cleanDestination;
              });
              if (destMatch) {
                if (!existingFlight.destinationBins) {
                  existingFlight.destinationBins = new Map();
                }
                if (!existingFlight.destinationBins.has(cleanDestination)) {
                  existingFlight.destinationBins.set(cleanDestination, {});
                }
                existingFlight.destinationBins
                  .get(cleanDestination)[binName] = flight.bags;
              }
            }

            existingFlight.binDistribution[binName] = 
              (existingFlight.binDistribution[binName] || 0) + flight.bags;
            existingFlight.brockBags += flight.bags;
            existingFlight.isOvernight = existingFlight.isOvernight || isOvernight;
            existingFlight.connectionTime = calculateConnectionTime(
              existingFlight.etd, 
              existingFlight.isOvernight
            );

            // Discrepancy check
            if (Math.abs(existingFlight.tdrBags - existingFlight.brockBags) > 0.001) {
              existingFlight.discrepancy = true;
            } else {
              existingFlight.discrepancy = false;
            }
          } else {
            // Flight only in Brock
            flightMap.set(normalizedFlightNumber, {
              originalFlightNumber: flight.flightNumber,
              flightNumber: normalizedFlightNumber,
              registration: "-",
              gate: flight.gate,
              etd: flight.est,
              destination: cleanDestination,
              tdrBags: 0,
              brockBags: flight.bags,
              binDistribution: { [binName]: flight.bags },
              discrepancy: true,
              onlyInBrock: true,
              isOvernight,
              connectionTime: calculateConnectionTime(flight.est, isOvernight),
            });
          }
        });
      });

      // 3) Convert to array and finalize
      const mergedFlights = Array.from(flightMap.values()).map(f => {
        // Cancelled flights get their own category (matches CombinedTab)
        const isCancelled = f.status?.toLowerCase().includes('cancel') || false;
        f.connectionCategory = isCancelled ? 'cancelled' : getConnectionCategory(f.connectionTime);

        // If multi-destination, mark the first row
        if (f.isMultiDestination && f.destinations) {
          f.isFirstDestination = true;
          f.destinationIndex = 0;
          f.totalDestinations = f.destinations.length;
        }
        return f;
      });

      // 4) Expand multi-dest flights into separate rows
      const processedFlights = [];
      mergedFlights.forEach(f => {
        if (f.isMultiDestination && f.destinations) {
          // Push the first row
          processedFlights.push(f);

          // Then push subsequent destination rows
          for (let i = 1; i < f.destinations.length; i++) {
            processedFlights.push({
              flightNumber: f.flightNumber,
              originalFlightNumber: f.originalFlightNumber,
              isMultiDestination: true,
              isFirstDestination: false,
              destinationIndex: i,
              totalDestinations: f.destinations.length,
              destination: f.destinations[i].city,
              tdrBags: f.destinations[i].bags,
              brockBagsByDest: f.destinationBins?.get(f.destinations[i].city) || {},
              connectionTime: f.connectionTime,
              connectionCategory: f.connectionCategory,
              isOvernight: f.isOvernight,
              discrepancy: f.discrepancy,
              gate: f.gate,
              etd: f.etd,
              registration: f.registration
            });
          }
        } else {
          processedFlights.push(f);
        }
      });

      // 5) Sort flights (hot/priority first, then normal, then cold)
      const hotAndPriority = processedFlights.filter(f =>
        (f.connectionCategory === 'hot' || f.connectionCategory === 'priority') &&
        (!f.isMultiDestination || f.isFirstDestination)
      );
      const normal = processedFlights.filter(f =>
        f.connectionCategory === 'normal' &&
        (!f.isMultiDestination || f.isFirstDestination)
      );
      const cold = processedFlights.filter(f =>
        f.connectionCategory === 'cold' &&
        (!f.isMultiDestination || f.isFirstDestination)
      );
      const cancelled = processedFlights.filter(f =>
        f.connectionCategory === 'cancelled' &&
        (!f.isMultiDestination || f.isFirstDestination)
      );

      hotAndPriority.sort((a,b)=>(a.etd||'').localeCompare(b.etd||''));
      normal.sort((a,b)=>(a.etd||'').localeCompare(b.etd||''));
      cold.sort((a,b)=>(a.etd||'').localeCompare(b.etd||''));
      cancelled.sort((a,b)=>(a.etd||'').localeCompare(b.etd||''));

      const sortedMain = [...hotAndPriority, ...normal, ...cold, ...cancelled];
      const result = [];
      sortedMain.forEach(f => {
        result.push(f);
        if (f.isMultiDestination && f.isFirstDestination) {
          // add the continuation rows in the right place
          const continuations = processedFlights.filter(x =>
            x.flightNumber === f.flightNumber && !x.isFirstDestination
          );
          result.push(...continuations);
        }
      });

      return result;
    };

    // Get the processed flights using the new logic
    const allFlights = mergeAndProcessData();
    
    // Get bin names (reverse sorted F->A)
    const binNames = Object.keys(brockData.bins || {}).sort().reverse();

    // Calculate terminating bags total
    const totalTerminatingBags = Object.values(brockData.bins || {}).reduce((total, bin) => {
      return total + (bin.terminating?.totalBags || 0);
    }, 0);

    // Build main flight table rows - simple approach like paste.txt
    const buildFlightRows = () => {
      return allFlights
        .map((flight) => {
          const isMultiDest = flight.isMultiDestination;
          const isFirst = flight.isFirstDestination;
          let cells = '';

          // Add rowspan cells only for first destination of multi-dest flights
          if (!isMultiDest || isFirst) {
            const rowSpan = flight.totalDestinations || 1;
            cells += `<td rowspan="${rowSpan}">${esc(displayFlightNumber(flight.originalFlightNumber || flight.flightNumber))}</td>`;
            cells += `<td rowspan="${rowSpan}">${esc(flight.registration || '-')}</td>`;
            cells += `<td rowspan="${rowSpan}">${esc(flight.gate || '')}</td>`;
            cells += `<td rowspan="${rowSpan}">${flight.connectionCategory === 'cancelled' ? 'Cancelled' : `${esc(flight.etd || '')}${flight.isOvernight ? ' (Overnight)' : ''}`}</td>`;
            cells += `<td rowspan="${rowSpan}" class="${flight.connectionTime < 0 ? 'negative-connection' : ''}">${flight.connectionCategory === 'cancelled' ? 'Cancelled' : esc(formatConnectionTime(flight.connectionTime))}</td>`;
          }

          // Always add destination and total bags columns
          cells += `<td>${esc(flight.destination || '')}</td>`;
          cells += `<td>${esc(flight.tdrBags || 0)}</td>`;
          
          // Handle bin distribution for multi-destination flights
          if (isMultiDest && !isFirst) {
            // For subsequent destinations, use the specific destination bins
            cells += binNames.map(binName => {
              const bagCount = flight.brockBagsByDest[binName] || 0;
              return `<td>${esc(bagCount)}</td>`;
            }).join('');
          } else {
            // For single destinations or first destination, use regular bin distribution
            cells += binNames.map(binName => `<td>${esc(flight.binDistribution[binName] || 0)}</td>`).join('');
          }
          
          const rowClass = `${flight.connectionCategory}-row`;
          return `<tr class="${rowClass}">${cells}</tr>`;
        })
        .join('');
    };

    // Date formatting
    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      let date;
      if (/^\d{8}$/.test(dateStr)) {
        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(4, 6);
        const day = dateStr.substring(6, 8);
        date = new Date(`${year}-${month}-${day}`);
      } else {
        date = new Date(dateStr);
      }
      if (isNaN(date.getTime())) return dateStr;
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      const yy = String(date.getFullYear()).substring(2);
      return `${mm}/${dd}/${yy}`;
    };

    const currentDate = new Date();
    const formattedCurrentDate = `${String(currentDate.getMonth() + 1).padStart(2, '0')}/${String(
      currentDate.getDate()
    ).padStart(2, '0')}/${String(currentDate.getFullYear()).substring(2)}`;

    const formattedTime = currentDate.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });

    // Generate the HTML - using the simpler approach from paste.txt
    printWindow.document.open();
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Flight ${esc(displayFlightNumber(tdrData.inbound?.flightNumber) || '')} - ${esc(formatDate(tdrData.inbound?.date))}</title>
          <style>
            @page { margin: 0.5in; }
            body { font-family: Arial, sans-serif; color: black; background: white; margin: 0; padding: 20px; }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-bottom: 15px;
              break-inside: auto;
            }
            thead { display: table-header-group; }
            tbody { page-break-inside: auto; }
            tr { 
              page-break-inside: avoid !important; 
              page-break-after: auto; 
              break-inside: avoid !important;
            }
            th, td { 
              border: 1px solid #ddd; 
              padding: 8px; 
              text-align: center; 
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            th { background-color: #f2f2f2; font-weight: bold; }
            
            /* Remove borders between grouped flight rows */
            tr[style*="border-bottom: none"] td { border-bottom: none; }
            tr[style*="border-top: none"] td { border-top: none; }
            
            .priority-row { background-color: rgba(249, 182, 18, 0.2); }
            .hot-row { background-color: rgba(255, 0, 0, 0.1); }
            .cold-row { background-color: rgba(0, 0, 255, 0.1); }
            .cancelled-row { background-color: rgba(255, 20, 147, 0.3); }
            .negative-connection { color: red; font-weight: bold; }
            .section-header { margin: 10px 0 5px 0; font-size: 16px; font-weight: bold; text-align: center; }
            h2 { color: #555; margin-bottom: 15px; text-align: center; }
            .page-footer { text-align: right; font-size: 12px; color: #666; margin-top: 20px; }
          </style>
        </head>
        <body>
          <h2>Inbound Flight: ${esc(displayFlightNumber(tdrData.inbound?.flightNumber) || '')} from ${esc(tdrData.inbound?.arrivingFrom || '')}</h2>

          <div class="section-header">Inbound Flight Details</div>
          <table>
            <thead>
              <tr>
                <th>Flight Number</th><th>Arriving From</th><th>Registry</th><th>Arrival Gate</th><th>ETA</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>${esc(displayFlightNumber(tdrData.inbound?.flightNumber) || '')}</td>
                <td>${esc(tdrData.inbound?.arrivingFrom || '')}</td>
                <td>${esc(tdrData.inbound?.registry || '')}</td>
                <td>${esc(tdrData.inbound?.arrivalGate || '')}</td>
                <td>${esc(tdrData.inbound?.eta || '')}</td>
              </tr>
            </tbody>
          </table>

          <div class="section-header">Outbound Flights</div>
          <table>
            <thead>
              <tr>
                <th>Flight #</th><th>Registration</th><th>Gate</th><th>ETD</th><th>Connection</th><th>Destination</th><th>Total Bags</th>
                ${binNames.map(bin => `<th>Bin ${esc(bin)}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${buildFlightRows()}
              <tr style="background-color: rgba(0, 0, 0, 0.05);">
                <td colspan="7" style="font-weight: bold; text-align: left;">Local Bags</td>
                ${binNames.map(binName => `<td style="font-weight: bold;">${esc(brockData.bins?.[binName]?.terminating?.totalBags || 0)}</td>`).join('')}
              </tr>
            </tbody>
          </table>

          <div class="page-footer">
            Flight Data Report - ${esc(displayFlightNumber(tdrData.inbound?.flightNumber) || '')} | ${formattedCurrentDate}, ${formattedTime}
          </div>
          
          <script>
            window.onload = function() {
              setTimeout(() => window.print(), 500);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <Button
      variant="outlined"
      startIcon={<PrintIcon />}
      onClick={handlePrint}
      sx={{
        marginBottom: '1rem',
        color: '#e2e2e2',
        borderColor: '#e2e2e2',
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          borderColor: '#fff',
        },
      }}
    >
      Print View
    </Button>
  );
};

export default PrintButton;